import { useEffect, useRef, useState } from 'react';
import { Button, RadioGroup } from '../../components/controls';
import { Icon, type IconName } from '../../components/Icon';
import { formatPounds } from '../../domain/calculator';
import type { ResultView } from '../../domain/contract';
import { api, ApiError, withRetry } from '../../lib/api';
import { FundingLogos } from '../Shell';
import { answersOf, totalFor } from '../state';
import type { ScreenProps } from './Steps';

const REVEAL_STEP_MS = 560;
const REVEAL_HOLD_MS = 750;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/** The programme reveal from design v4: six checks tick in, then the result. */
export function Checking({ flow, settings, update, go, headingRef }: ScreenProps) {
  const [tick, setTick] = useState(0);
  const [failed, setFailed] = useState(false);
  const started = useRef(false);
  const view = flow.evaluation?.view ?? null;
  const reduced = prefersReducedMotion();

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    withRetry(() => api.evaluate({ answers: { ...answersOf(flow), companyNumber: null }, hires: flow.hires }))
      .then((evaluation) => update({ evaluation }))
      .catch((error) => {
        if (error instanceof ApiError && error.status === 422) go('role');
        else setFailed(true);
      });
  }, [flow, update, go]);

  useEffect(() => {
    if (reduced) return;
    if (tick >= 6) return;
    const timer = setTimeout(() => setTick((t) => (view || t < 5 ? t + 1 : t)), REVEAL_STEP_MS);
    return () => clearTimeout(timer);
  }, [tick, view, reduced]);

  useEffect(() => {
    if (!view) return;
    if (reduced) {
      go('result');
      return;
    }
    if (tick < 6) return;
    const timer = setTimeout(() => go('result'), REVEAL_HOLD_MS);
    return () => clearTimeout(timer);
  }, [tick, view, reduced, go]);

  const band = settings.employeeBands.find((b) => b.id === flow.employeeBand);
  const confirmed = Object.values(flow.checks).filter(Boolean).length;
  const eligible = view?.result === 'eligible' ? view : null;
  const region = flow.evaluation?.regionName ?? (flow.postcodeLookup?.status === 'resolved' ? flow.postcodeLookup.regionName : null);
  const inFunded = flow.postcodeLookup?.status === 'resolved' ? flow.postcodeLookup.inFundedRegion : true;
  const matched = settings.roles.some((r) => r.title.toLowerCase() === flow.jobTitle.trim().toLowerCase());
  const rows: [string, string, boolean][] = [
    ['Company size', `${band?.label ?? 'Unknown'} employees`, flow.employeeBand === '250+'],
    ['Location', inFunded ? (region ?? 'Unknown') : 'Outside the funded regions', !inFunded],
    ['Role', matched ? flow.jobTitle.trim() : `${flow.jobTitle.trim() || 'Other'}, not on the funded list yet`, !matched],
    ['Eligibility checklist', `${confirmed} of 7 confirmed`, confirmed < 7],
    [
      'Programme match',
      eligible
        ? `${eligible.programmeName}, Cohort ${eligible.cohortNumber}`
        : view?.result === 'check_needed'
          ? 'To confirm on a short call'
          : 'No funded match this round',
      !eligible,
    ],
    ['Package value', eligible ? `${formatPounds(eligible.package.firstHire)} for your first hire` : 'Not calculated', !eligible],
  ];

  return (
    <section className="sec">
      <div aria-live="polite">
        <div className="eyebrow mute">One moment</div>
        <h1 className="disp h2" style={{ marginTop: 8 }} ref={headingRef} tabIndex={-1}>
          Checking your eligibility
        </h1>
        <p className="s15 mute" style={{ lineHeight: 1.5, marginTop: 8 }}>
          Matching your answers to the funded programmes and working out your package.
        </p>
        {failed ? (
          <div className="notice" role="alert" style={{ marginTop: 20 }}>
            We couldn't check your answers just now.{' '}
            <button type="button" className="lnk" style={{ width: 'auto', display: 'inline', minHeight: 0 }} onClick={() => window.location.reload()}>
              Try again
            </button>
          </div>
        ) : null}
        <div style={{ height: 4, borderRadius: 999, background: 'rgba(0,0,0,.08)', marginTop: 20, overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${Math.min(100, (tick / 6) * 100)}%`,
              background: '#629677',
              borderRadius: 999,
              transition: 'width 520ms cubic-bezier(.2,0,0,1)',
            }}
          />
        </div>
        <ol style={{ marginTop: 20, display: 'grid' }}>
          {rows.map(([label, detail, flag], i) => {
            const state = i < tick ? (flag ? 'flag' : 'done') : i === tick ? 'active' : 'pending';
            const icon: IconName = state === 'done' ? 'check' : state === 'flag' ? 'alert-circle' : state === 'active' ? 'loader' : 'dot';
            return (
              <li
                key={label}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '28px minmax(0,1fr)',
                  gap: 14,
                  alignItems: 'center',
                  minHeight: 56,
                  borderBottom: '1px solid rgba(0,0,0,.08)',
                }}
              >
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: state === 'done' ? '#629677' : state === 'flag' ? '#F7F8FA' : 'transparent',
                    color: state === 'done' ? '#fff' : '#000',
                    border: state === 'pending' ? '1.5px solid rgba(0,0,0,.15)' : state === 'flag' ? '1px solid rgba(0,0,0,.22)' : '1.5px solid transparent',
                  }}
                >
                  <Icon name={icon} size={state === 'active' ? 20 : 16} className={state === 'active' ? 'spin' : undefined} />
                </span>
                <div style={{ opacity: state === 'pending' ? 0.4 : 1, transition: 'opacity 200ms' }}>
                  <div className="s15" style={{ fontWeight: 600 }}>
                    {label}
                  </div>
                  {state === 'done' || state === 'flag' ? (
                    <div className="s13 mute fade" style={{ marginTop: 2 }}>
                      {detail}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

export function PackagePanel({
  view,
  hires,
  onHires,
  maxHires,
  minHires,
}: {
  view: Extract<ResultView, { result: 'eligible' }>;
  hires: number;
  onHires?: (n: number) => void;
  minHires: number;
  maxHires: number;
}) {
  const pkg = view.package;
  const total = totalFor(pkg.firstHire, pkg.additionalHire, hires);
  const multi = hires > 1;
  const worth = multi
    ? `For a hiring package worth ${formatPounds(total)} across ${hires} hires.`
    : pkg.overClaim
      ? `For a hiring package worth ${formatPounds(pkg.firstHire)}, that is over £7,000 of support.`
      : `For a hiring package worth ${formatPounds(pkg.firstHire)}.`;
  return (
    <div className="pack">
      <div className="s14 sub">You only pay</div>
      <div
        className="num"
        style={{ fontWeight: 700, fontSize: 'clamp(40px,11vw,52px)', lineHeight: 1.05, letterSpacing: '-.02em', marginTop: 6 }}
        aria-live="polite"
      >
        {formatPounds(pkg.feePerHire * hires)}
      </div>
      <div className="s13 sub" style={{ marginTop: 6 }}>
        {multi ? `${formatPounds(pkg.feePerHire)} per hire, only if you hire.` : 'Per hire, only if you hire.'}
      </div>
      <div className="s14" style={{ marginTop: 10 }}>
        {worth}
      </div>
      {onHires ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 16,
            paddingTop: 14,
            borderTop: '1px solid rgba(255,255,255,.16)',
          }}
        >
          <span className="s14" id="hires-label">
            Planning more than one hire?
          </span>
          <div
            role="group"
            aria-labelledby="hires-label"
            style={{
              display: 'grid',
              gridTemplateColumns: '44px 32px 44px',
              alignItems: 'center',
              border: '1px solid rgba(255,255,255,.3)',
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            <button type="button" className="step" aria-label="Fewer hires" disabled={hires <= minHires} onClick={() => onHires(Math.max(minHires, hires - 1))}>
              <Icon name="minus" size={16} />
            </button>
            <span
              className="num"
              style={{ textAlign: 'center', fontSize: 16, fontWeight: 600 }}
              aria-live="polite"
              aria-label={`${hires} ${hires === 1 ? 'hire' : 'hires'}`}
            >
              {hires}
            </span>
            <button type="button" className="step" aria-label="More hires" disabled={hires >= maxHires} onClick={() => onHires(Math.min(maxHires, hires + 1))}>
              <Icon name="plus" size={16} />
            </button>
          </div>
        </div>
      ) : null}
      {multi ? (
        <div className="num" style={{ display: 'grid', marginTop: 10 }}>
          <div className="s14" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0' }}>
            <span>First hire</span>
            <span>{formatPounds(pkg.firstHire)}</span>
          </div>
          <div className="s14" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0' }}>
            <span>Each additional hire</span>
            <span>{formatPounds(pkg.additionalHire)}</span>
          </div>
          <div
            className="s15"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              padding: '10px 0 0',
              borderTop: '1px solid rgba(255,255,255,.4)',
              fontWeight: 700,
            }}
          >
            <span>Total for {hires} hires</span>
            <span>{formatPounds(total)}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function Result({ flow, settings, wide, update, go, headingRef }: ScreenProps) {
  const view = flow.evaluation?.view;
  if (!view) return null;

  return (
    <section className="sec">
      {view.result === 'eligible' ? (
        <>
          <span
            style={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px 5px 8px',
              border: '1px solid #629677',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Icon name="check" size={14} style={{ color: '#629677' }} /> Eligible · Cohort {view.cohortNumber}
          </span>
          <h1 className="disp hr" style={{ marginTop: 14 }} ref={headingRef} tabIndex={-1}>
            Good news, you're eligible for{' '}
            <em className="em" style={{ color: '#629677' }}>
              {view.programmeName}
            </em>
            .
          </h1>
          <p className="s15 mute" style={{ lineHeight: 1.55, marginTop: 10 }}>
            {view.programmeDescription}
          </p>
          <PackagePanel view={view} hires={flow.hires} onHires={(hires) => update({ hires })} minHires={settings.hires.min} maxHires={settings.hires.max} />
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginTop: 28 }}>What you'll get with {view.programmeName}</h2>
            <ul style={{ marginTop: 10, display: 'grid' }}>
              {view.package.lines.map((line) => (
                <li key={line.id} className="line">
                  <Icon name="check" size={18} style={{ color: '#629677', marginTop: 1 }} />
                  <div>
                    <div className="s15" style={{ fontWeight: 600, lineHeight: 1.35 }}>
                      {line.label}
                    </div>
                    <div className="s13 mute" style={{ lineHeight: 1.45, marginTop: 2 }}>
                      {line.description}
                    </div>
                  </div>
                  <span className="s14 num" style={{ paddingTop: 1 }}>
                    {line.value == null ? 'Included' : formatPounds(line.value)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginTop: 28 }}>What happens next</h2>
          <ol className="steps">
            {settings.nextSteps.map((text, i) => (
              <li key={text}>
                <span className="sn">{i + 1}</span>
                <span>{text}</span>
              </li>
            ))}
          </ol>
        </>
      ) : view.result === 'check_needed' ? (
        <>
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: '#F7F8FA',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="phone" size={20} />
          </span>
          <h1 className="disp hr" style={{ marginTop: 16 }} ref={headingRef} tabIndex={-1}>
            Let's have a quick chat.
          </h1>
          <p className="s15 mute" style={{ lineHeight: 1.55, marginTop: 10 }}>
            Your role may still qualify. Book a short call and we'll check the details with you.
          </p>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginTop: 24 }}>What we'll check together</h2>
          <ul style={{ marginTop: 8, display: 'grid' }}>
            {view.topics.map((topic) => (
              <li key={topic} className="l2">
                <Icon name="circle-help" size={18} style={{ color: '#4A4A4A', marginTop: 1 }} />
                <span>{topic}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <h1 className="disp" style={{ fontSize: 'clamp(22px,5vw,28px)', lineHeight: 1.25, letterSpacing: '-.02em' }} ref={headingRef} tabIndex={-1}>
            This round isn't the right fit, but we can still help.
          </h1>
          <p className="s15 mute" style={{ lineHeight: 1.55, marginTop: 10 }}>
            {view.reason}
          </p>
          <div style={{ marginTop: 20 }}>
            <RadioGroup
              label="What would you like to do?"
              variant="dot"
              options={[
                { id: 'waitlist', label: 'Join the waitlist for the next intake' },
                { id: 'talk', label: 'Talk to us about hiring with PeopleXYZ' },
              ]}
              value={flow.waitOption}
              onSelect={(id) => update({ waitOption: id as 'waitlist' | 'talk' })}
            />
          </div>
        </>
      )}
      <div style={{ marginTop: 28 }}>
        <Button onClick={() => go('secure')}>
          {view.result === 'eligible' ? 'Secure my place' : view.result === 'check_needed' ? 'Book my call' : 'Continue'}
        </Button>
        {view.result === 'eligible' ? (
          <p className="s13 mute" style={{ textAlign: 'center', marginTop: 10 }}>
            We'll hold your place for {settings.holdDays} days while we agree terms.
          </p>
        ) : null}
      </div>
      <p className="s13 mute" style={{ marginTop: 20 }}>
        {settings.fundingLine}
      </p>
      {wide ? null : (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,.08)' }}>
          <FundingLogos />
        </div>
      )}
    </section>
  );
}
